import React, { useState, useEffect } from "react";
import axios from "axios";
import { Play, Youtube, Facebook } from "lucide-react";
import { API, HERO_BG, PageHero, LiveBadge, LiveAlertOptIn, useYouTubeLive } from "../shared";

const MediaPage = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const live = useYouTubeLive();

  useEffect(() => {
    axios.get(`${API}/media`).then(res => {
      setMedia(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <div className="pt-20 fade-in">
      <PageHero eyebrow="Watch & Listen" title="Sermons" italic bg={HERO_BG.media} testId="media-title" />

      <section className="py-20 bg-neutral-900 text-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <p className="heading-caps text-xs text-white/40 tracking-[0.2em]">Live Stream</p>
                <LiveBadge />
              </div>
              <h2 className="text-3xl md:text-4xl mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                {live.is_live ? <>We&apos;re <span className="italic">Live Now</span></> : <>Watch Live<br /><span className="italic">Every Sunday</span></>}
              </h2>
              <p className="text-white/60 mb-8">
                {live.is_live ? "Join us right now — the live service is streaming." : "Can't make it in person? Join us online every Sunday at 10:00 AM PST."}
              </p>
              <div className="flex flex-wrap gap-4">
                <a href={live.is_live && live.watch_url ? live.watch_url : "https://www.youtube.com/@whconation9174"} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 btn-primary" data-testid="youtube-live">
                  <Youtube size={18} /> {live.is_live ? "Watch Live" : "YouTube"}
                </a>
                <a href="https://www.facebook.com/whcoministries/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 btn-outline" data-testid="facebook-live">
                  <Facebook size={18} /> Facebook
                </a>
              </div>
            </div>
            <div className="video-container bg-black rounded-none">
              <iframe src={live.is_live && live.video_id ? `https://www.youtube.com/embed/${live.video_id}?autoplay=1` : "https://www.youtube.com/embed/_CILLS62wiQ"}
                title="WHCO Live Stream"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="mb-16">
            <p className="heading-caps text-xs text-black/40 mb-4 tracking-[0.2em]">Archive</p>
            <h2 className="text-3xl md:text-4xl" style={{ fontFamily: 'Playfair Display, serif' }}>Recent Sermons</h2>
          </div>
          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-2 border-black border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : media.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12" data-testid="media-list">
              {media.map((item) => (
                <div key={item.media_id} className="group" data-testid={`media-${item.media_id}`}>
                  <a href={item.url} target="_blank" rel="noopener noreferrer" className="block">
                    <div className="aspect-video bg-neutral-100 mb-6 overflow-hidden relative">
                      {item.thumbnail_url ? (
                        <img src={item.thumbnail_url} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-black"><Play size={48} className="text-white/30" /></div>
                      )}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                        <Play size={48} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                    <p className="text-xs text-black/40 mb-2">{new Date(item.date).toLocaleDateString()}</p>
                    <h3 className="text-xl mb-2 group-hover:text-red-700 transition-colors" style={{ fontFamily: 'Playfair Display, serif' }}>{item.title}</h3>
                    {item.speaker && <p className="text-sm text-black/50">by {item.speaker}</p>}
                  </a>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-neutral-100">
              <Play size={48} className="mx-auto text-black/20 mb-4" />
              <p className="text-black/40">Sermons coming soon</p>
            </div>
          )}
          <div className="text-center mt-16">
            <a href="https://www.youtube.com/@whconation9174" target="_blank" rel="noopener noreferrer" className="btn-dark inline-flex items-center gap-2">
              <Youtube size={18} /> View All on YouTube
            </a>
          </div>
        </div>
      </section>

      <section className="bg-black px-8 py-24" data-testid="media-live-alert">
        <div className="max-w-5xl mx-auto border border-white/10">
          <LiveAlertOptIn variant="dark" source="media_page" testId="media-live-optin"
            eyebrow="Live Alerts"
            title={<>Get notified when we go <span className="italic">live</span>.</>}
            subtitle="One-tap YouTube link, straight to your inbox. No spam — only Sunday services and special broadcasts."
          />
        </div>
      </section>
    </div>
  );
};

export default MediaPage;
